import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useInterval } from "../timing/use-interval";

import { Helmet } from "react-helmet-async";
import {
	fetchPriorityLists,
	removePriorityListsByIds,
	selectDeletingPriorityList,
	selectPriorityListsByContext,
	selectPriorityListsByIds,
} from "./slice";
import { parsePriorityFilters } from "./filters";
import { updateSearchParamsWithFilters } from "../filters/filters";
import Layout from "../../ui/Layout";
import NavMain from "../../app/NavMain";
import ScrollablePage from "../../ui/ScrollablePage";
import Paper from "../../ui/Paper";
import ErrorMsg from "../../ui/ErrorMsg";
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner";
import PriorityListTable from "./PriortyListTable";
import CreatePriorityList from "./CreatePriorityList";
import Button from "../../ui/Button";
import { faPlus, faPlay } from "@fortawesome/free-solid-svg-icons";
import DeleteConfirmationDialog from "../../ui/DeleteConfirmationDialog";
import { API_BASE_URL } from "../../app/config";

import { getToken } from "../iam/middleware";
import {
	Header,
	Overview,
	Actions,
	Action,
	ActionLink,
} from "../../ui/PageHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";

const PAGE_SIZE = 200; // Default initial and subsequent page size
const LIVE_UPDATE_INTERVAL = 30000; // ms

/* PriorityList renders a page containing a filter- and sort-able table
 * of all data priority lists matching the criteria in the current URL.  */
const PrioritiesLists = () => {
	const dispatch = useDispatch();
	const [isAddingPriorityList, setIsAddingPriorityList] = useState(false);
	const [selectedLists, setSelectedLists] = useState([]); // List IDs

	// URL query param for selected lists, e.g. to pass on
	// to deployment page
	const selParam =
		selectedLists.length > 0
			? `?priority_lists=${selectedLists.join(",")}`
			: "";

	const inProgressDeletions = useSelector((state) =>
		selectDeletingPriorityList(state),
	);
	// we get any matching priority lists
	const priorityMatches = useSelector((state) =>
		selectPriorityListsByContext(state, "root"),
	);
	// We get the full objects from the ids that match
	const priorities = useSelector((state) =>
		selectPriorityListsByIds(state, priorityMatches?.ids || []),
	);

	// Parse parameters into filter object (which drives filters UI state)
	// Note: We are using the URL as the "state" here, and updates
	// perform a history replacement (so as not to mess up "back" button
	// behaviour too much - though we may want to apply a heuristic like
	// small changes replace history, big changes push history)
	const location = useLocation();

	const navigate = useNavigate();
	const urlParams = new URLSearchParams(location.search);
	const filter = parsePriorityFilters(urlParams);
	updateSearchParamsWithFilters(urlParams, filter);

	useEffect(() => {
		dispatch(
			fetchPriorityLists({
				filter,
				page: { offset: 0, limit: PAGE_SIZE },
				context: ["root"],
			}),
		);
	}, [urlParams.toString()]);

	// How we apply a filter update. Filter state is entirely in the URL query parameters
	const setFilter = (filter = {}) => {
		const updUrlParams = updateSearchParamsWithFilters(urlParams, filter);
		navigate({
			pathname: location.pathname,
			search: `?${updUrlParams}`,
		});
	};

	// How many more are available to load?
	const loadableCount = Math.min(
		PAGE_SIZE,
		priorities?.length && priorityMatches?.page?.total_count
			? priorityMatches.page.total_count - priorities.length
			: 50,
	);

	// Event handler for 'Show xxx more' button that was clicked
	const handleLoadMore = () => {
		const lastIndex = priorities?.length;

		const page = {
			offset: lastIndex,
			limit: loadableCount,
		};
		dispatch(fetchPriorityLists({ filter, page, context: ["root"] }));
	};

	const handleAddPriorityList = () => {
		setIsAddingPriorityList(!isAddingPriorityList);
	};

	const handleCloseAddPriorityList = () => {
		setIsAddingPriorityList(false);
	};

	// Close on ESC
	useEffect(() => {
		const handler = (e) => {
			if (e.key == "Escape") {
				setIsAddingPriorityList(false);
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	});

	const toggleSelectedList = (priorityID) => {
		if (selectedLists.includes(priorityID)) {
			setSelectedLists(selectedLists.filter((p) => p !== priorityID));
		} else {
			setSelectedLists([...selectedLists, priorityID]);
		}
	};

	const handleDeletePriorityLists = (selectedListIDs = []) => {
		setSelectedLists(selectedLists.filter((p) => !selectedListIDs.includes(p)));
		dispatch(removePriorityListsByIds(selectedListIDs));
	};

	// Poll at an interval, based on whether "Update live" is checked
	// As a hack, for non-live updates, set a long (but non-infinite, which breaks) interval, e.g. a week
	useInterval(
		() => {
			dispatch(
				fetchPriorityLists({
					filter,
					page: { offset: 0, limit: PAGE_SIZE },
					context: ["root"],
				}),
			);
		},
		LIVE_UPDATE_INTERVAL,
		false,
	);

	return (
		<Layout>
			<NavMain key="nav-left" />
			<Helmet>
				<title>Priority Lists - Astrosite</title>
			</Helmet>
			<ScrollablePage>
				{priorities.length === 0 &&
					priorityMatches?._loadStatus === "loading" && (
						<Backdrop>
							<LoadingSpinner />
						</Backdrop>
					)}
				{inProgressDeletions?._saveStatus === "deleting" && (
					<Backdrop>
						<LoadingSpinner />
					</Backdrop>
				)}
				<Header>
					<Overview>
						<h1>Priority Lists</h1>
						<p>
							{priorities.length > 0 && (
								<span>
									Showing <strong>{priorityMatches?.page?.end_index}</strong>
									{priorityMatches &&
										priorityMatches.page &&
										priorityMatches.page.total_count && (
											<span>
												{" "}
												of{" "}
												<strong className="count total_count">
													{priorityMatches.page.total_count}
												</strong>
											</span>
										)}{" "}
									lists
								</span>
							)}
							{priorityMatches?._loadStatus == "loading" && " loading..."}
						</p>
					</Overview>
					<Actions>
						{selectedLists?.length > 0 && (
							<Action>
								<DeleteConfirmationDialog
									text="Delete selected"
									item={{ name: `${selectedLists.length} selected items` }}
									onDelete={() => handleDeletePriorityLists(selectedLists)}
								/>
							</Action>
						)}
						<Action>
							<Button onClick={handleAddPriorityList} icon={faPlus}>
								Add new list
							</Button>
						</Action>
						{/* Tasking is hidden for the moment, needs a re-think.
						<Action>
							<Link to={"/playlists/deploy-manual" + selParam}>
								<Button icon={faPlay}>
									{selectedLists.length > 0
										? `Schedule selected (${selectedLists.length})`
										: "Schedule active"}
								</Button>
							</Link>
						</Action>
						*/}
						{selectedLists?.length > 0 && (
							<Action>
								<ActionLink
									href={getExportJSONURL([...selectedLists])}
									target="_blank"
								>
									<span>{selectedLists.length} selected as Sched. JSON</span>
									<FontAwesomeIcon icon={faDownload} />
								</ActionLink>
							</Action>
						)}
					</Actions>
				</Header>
				{priorityMatches?._loadStatus == "rejected" && (
					<ErrorMsg
						msg={"There was a problem fetching the priority lists"}
						showIcon={true}
					/>
				)}

				<Paper>
					<PriorityListTable
						priorities={priorities}
						filter={filter}
						setFilter={setFilter}
						selectedLists={selectedLists}
						toggleSelectedList={toggleSelectedList}
						setSelectedLists={setSelectedLists}
					/>
					{priorities?.length == 0 &&
						priorityMatches?._loadStatus == "idle" && (
							<p>There are no priority lists matching your criteria.</p>
						)}
					<footer className="actions">
						{priorities.length > 0 &&
							priorities.length < priorityMatches?.page?.total_count && (
								<Button
									onClick={handleLoadMore}
									disabled={priorityMatches?._loadStatus == "loading"}
								>
									{priorityMatches?._loadStatus == "loading"
										? " loading..."
										: `Show ${loadableCount} more`}
								</Button>
							)}
					</footer>
				</Paper>
				<div></div>
			</ScrollablePage>
			{isAddingPriorityList && (
				<CreatePriorityList handleCancel={handleCloseAddPriorityList} />
			)}
		</Layout>
	);
};
export const getExportJSONURL = (
	listIDs, // Array<ObservationID>
) =>
	`${API_BASE_URL}/api/priorities/for-scheduler?download=true&priority_lists=${encodeURIComponent(
		(listIDs || []).join(","),
	)}&token=${encodeURIComponent(getToken())}`;

export default PrioritiesLists;
